// Listado de pre-lanzamientos — ADR-2026-09-06-lanzamientos-modelo-de-datos.
//
// Dos grupos: los que se están preparando y los ya publicados. La acción de la
// pantalla es entrar a uno; crear es el atajo de arriba.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Rocket, Plus, Loader2, ChevronRight, Calendar, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useBrandProfile } from '../../hooks/useBrandProfile';
import { usePreLanzamientos } from '../../hooks/useFuncionesAvanzadas';
import {
  crearLanzamiento,
  fetchLanzamientos,
  type CanalLanzamiento,
  type EstadoLanzamiento,
  type Lanzamiento,
} from './lanzamientosApi';
// Los destinos y sus reglas viven en un solo lugar: este selector, el de
// "Enviar a lanzamiento" y la validación de publicar leen la misma lista.
import { CANALES, canalesPorGrupo, creaProductos, nombresDeDestinos } from './canales';
import { useMercadoLibreDisponible } from '../../hooks/useMercadoLibreDisponible';
import IconoDeCanal from './IconoDeCanal';
import {
  cerrarRecorridoEnCurso,
  pasosDelDestino,
  pasosDelInicio,
  useRecorrido,
} from './recorridos';

const ESTADOS: Record<EstadoLanzamiento, { texto: string; clase: string }> = {
  borrador: { texto: 'En preparación', clase: 'bg-neutral-100 text-neutral-600' },
  publicando: { texto: 'Publicando', clase: 'bg-blue-50 text-blue-700' },
  publicado: { texto: 'Publicado', clase: 'bg-green-50 text-green-700' },
  con_errores: { texto: 'Con errores', clase: 'bg-red-50 text-red-700' },
};

export default function Lanzamientos() {
  const navigate = useNavigate();
  const { profile, currentBrandId } = useBrandProfile();
  const habilitado = usePreLanzamientos();
  const [lanzamientos, setLanzamientos] = useState<Lanzamiento[]>([]);
  const [cargando, setCargando] = useState(true);
  const [creando, setCreando] = useState(false);
  // `?nuevo=1`: se llegó con la intención de crear uno (el botón "Publicar en tu
  // tienda" de Creaciones), así que el modal arranca abierto. Se lee UNA vez al
  // montar y el parámetro se limpia: recargar o volver atrás no lo reabre.
  const [params, setParams] = useSearchParams();
  const [abrirNuevo] = useState(() => params.get('nuevo') === '1');
  useEffect(() => {
    if (!params.has('nuevo')) return;
    const sin = new URLSearchParams(params);
    sin.delete('nuevo');
    setParams(sin, { replace: true });
  }, [params, setParams]);

  const companyId = profile?.id_company ?? null;

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setCargando(true);
    setLanzamientos(await fetchLanzamientos(companyId, currentBrandId));
    setCargando(false);
  }, [companyId, currentBrandId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const { preparando, publicados } = useMemo(
    () => ({
      preparando: lanzamientos.filter((l) => l.estado !== 'publicado'),
      publicados: lanzamientos.filter((l) => l.estado === 'publicado'),
    }),
    [lanzamientos],
  );

  // El primer cartel del módulo: con la lista vacía, señala "Nuevo lanzamiento"
  // (ADR-2026-10-01-recorridos-de-tienda-online). Antes del `return` temprano: un
  // hook no puede quedar detrás de un `if`. Espera solo a que se cierre el anuncio
  // de la sección y el modal de crear, si se llegó con `?nuevo=1`.
  useRecorrido(
    'tienda_tour_inicio',
    habilitado && !cargando && !!companyId && lanzamientos.length === 0,
    pasosDelInicio,
  );

  if (!habilitado) {
    return (
      <div className="mx-auto max-w-2xl py-16 px-6 text-center">
        <AlertCircle className="mx-auto w-8 h-8 text-neutral-300" />
        <h1 className="mt-4 text-lg font-semibold text-brand-black">
          Los pre-lanzamientos están desactivados
        </h1>
        <p className="mt-2 text-sm text-neutral-500">
          Se activan desde Configuración, en Funciones avanzadas.
        </p>
        <button
          type="button"
          onClick={() => navigate('/configuracion', { state: { section: 'funcionesAvanzadas' } })}
          className="mt-5 rounded-xl bg-brand-black py-2.5 px-5 text-sm font-medium text-white"
        >
          Ir a Configuración
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl py-6 px-4 md:py-10 md:px-8">
      {/* El botón va en el renglón del título, no al costado del bloque entero: con la
          bajada abajo, alinearlo al bloque lo dejaba flotando más alto que el título. */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold text-brand-black md:text-2xl">Tienda online</h1>
        <NuevoLanzamiento
          companyId={companyId}
          brandId={currentBrandId}
          creando={creando}
          setCreando={setCreando}
          abiertoAlInicio={abrirNuevo}
          onCreado={(l) => navigate(`/lanzamientos/${l.id}`)}
        />
      </div>
      <p className="mt-1 text-sm text-neutral-500">
        Juntá productos con sus fotos y publicalos todos juntos.
      </p>

      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-20 text-sm text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      ) : lanzamientos.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-neutral-300 py-16 px-6 text-center">
          <Rocket className="mx-auto w-8 h-8 text-neutral-300" />
          <p className="mt-4 text-sm font-medium text-brand-black">Todavía no creaste ninguno</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-neutral-500">
            Creá un lanzamiento y después mandale imágenes desde el botón “Enviar a lanzamiento”
            de cada foto.
          </p>
        </div>
      ) : (
        <div className="mt-8 space-y-8">
          <Grupo titulo="En preparación" items={preparando} onAbrir={(id) => navigate(`/lanzamientos/${id}`)} />
          <Grupo titulo="Publicados" items={publicados} onAbrir={(id) => navigate(`/lanzamientos/${id}`)} />
        </div>
      )}
    </div>
  );
}

function Grupo({
  titulo,
  items,
  onAbrir,
}: {
  titulo: string;
  items: Lanzamiento[];
  onAbrir: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <section>
      <h2 className="text-xs font-medium uppercase tracking-wide text-neutral-400">{titulo}</h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((l) => {
          const e = ESTADOS[l.estado];
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => onAbrir(l.id)}
              className="group flex min-h-[180px] flex-col rounded-2xl border border-neutral-200 bg-surface-card py-5 px-5 text-left shadow-sm transition-[border-color,box-shadow] hover:border-neutral-300 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-2">
                <span className={`rounded-full py-1 px-2.5 text-[11px] font-medium ${e.clase}`}>
                  {e.texto}
                </span>
                <ChevronRight className="w-4 h-4 shrink-0 text-neutral-300 group-hover:text-neutral-500" />
              </div>
              <p className="mt-4 line-clamp-2 text-base font-medium leading-snug text-brand-black">
                {l.nombre}
              </p>
              {/* El pie queda apoyado abajo: con `mt-auto` todas las cards de la fila
                  cierran a la misma altura aunque el nombre ocupe uno o dos renglones. */}
              <div className="mt-auto pt-4">
                <p className="text-xs text-neutral-500">
                  {nombresDeDestinos(l.destinos)} · {l.productos} producto{l.productos === 1 ? '' : 's'} ·{' '}
                  {l.imagenes} foto{l.imagenes === 1 ? '' : 's'}
                </p>
                {l.fechaLanzamiento && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-neutral-400">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(`${l.fechaLanzamiento}T00:00:00`).toLocaleDateString('es-AR', {
                      day: 'numeric',
                      month: 'long',
                    })}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function NuevoLanzamiento({
  companyId,
  brandId,
  creando,
  setCreando,
  abiertoAlInicio = false,
  onCreado,
}: {
  companyId: string | null;
  brandId: string | null;
  creando: boolean;
  setCreando: (v: boolean) => void;
  abiertoAlInicio?: boolean;
  onCreado: (l: Lanzamiento) => void;
}) {
  const [abierto, setAbierto] = useState(abiertoAlInicio);
  const [nombre, setNombre] = useState('');
  /**
   * Los destinos elegidos. Las tiendas que crean productos solas (Tienda Nube y Mercado Libre) se
   * pueden elegir juntas; el resto va solo (ADR-2026-09-30-lanzamiento-a-varias-tiendas).
   */
  const [elegidos, setElegidos] = useState<CanalLanzamiento[]>(['tiendanube']);
  // Se consulta con la página, no al abrir: si no, Mercado Libre aparecía un segundo después en el
  // modal (prueba del 2/10). Es una consulta por visita.
  const ml = useMercadoLibreDisponible(brandId);
  const elegir = (id: CanalLanzamiento) =>
    setElegidos((antes) => {
      if (!creaProductos(id) || !antes.every(creaProductos)) return [id];
      if (!antes.includes(id)) return [...antes, id];
      // Siempre queda al menos una.
      return antes.length > 1 ? antes.filter((x) => x !== id) : antes;
    });
  /** Lo que falta, recién después de intentar crear. Ver el comentario del botón. */
  const [falta, setFalta] = useState<string | null>(null);
  const campoNombre = useRef<HTMLInputElement>(null);
  // El cartel del destino, la primera vez que se abre el modal. Es lo único que el
  // modal no dice: que el destino no se cambia después.
  useRecorrido('tienda_tour_destino', abierto, pasosDelDestino, { dentroDeUnModal: true });

  const crear = async () => {
    if (!nombre.trim()) {
      // El botón ya NO está deshabilitado: ver el comentario de abajo.
      setFalta('Escribí un nombre para el lanzamiento.');
      campoNombre.current?.focus();
      return;
    }
    if (!companyId) return;
    setCreando(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const l = await crearLanzamiento({
      companyId,
      brandId,
      nombre,
      canal: elegidos[0]!,
      destinos: elegidos,
      userId: user?.id ?? null,
    });
    setCreando(false);
    if (l) onCreado(l);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          // Si el cartel de "Empezá por acá" está señalando este botón, ya cumplió:
          // se cierra para no quedar encima del modal.
          cerrarRecorridoEnCurso();
          setAbierto(true);
        }}
        data-tour="tienda-nuevo"
        className="flex shrink-0 items-center gap-2 rounded-xl bg-brand-black py-2.5 px-5 text-sm font-medium text-white"
      >
        <Plus className="w-4 h-4" />
        <span className="hidden sm:inline">Nuevo lanzamiento</span>
        <span className="sm:hidden">Nuevo</span>
      </button>

      {abierto && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 sm:items-center">
          {/* Con los destinos agrupados el modal puede pasar el alto de un
              teléfono chico: scrollea adentro en vez de dejar "Crear" abajo del
              borde de la pantalla. */}
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="titulo-nuevo-lanzamiento"
            className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-3xl"
          >
            <h2 id="titulo-nuevo-lanzamiento" className="text-lg font-semibold text-brand-black">
              Nuevo lanzamiento
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              Elegí a dónde va. Después le sumás productos y fotos.
            </p>

            <label
              htmlFor="nombre-del-lanzamiento"
              className="mt-5 block text-xs font-medium text-neutral-500"
            >
              Nombre
            </label>
            <input
              id="nombre-del-lanzamiento"
              ref={campoNombre}
              autoFocus
              value={nombre}
              onChange={(e) => {
                setNombre(e.target.value);
                // Se limpia al escribir: dejar el error rojo mientras la persona
                // está justo arreglándolo la reta por algo que ya resolvió.
                if (falta) setFalta(null);
              }}
              aria-invalid={falta ? true : undefined}
              aria-describedby={falta ? 'falta-el-nombre' : undefined}
              placeholder="Colección primavera"
              className={`mt-1 w-full rounded-xl border py-2.5 px-4 text-sm text-brand-black outline-none ${
                falta ? 'border-red-400 focus:border-red-400' : 'border-neutral-200 focus:border-neutral-400'
              }`}
            />
            {falta && (
              <p id="falta-el-nombre" role="alert" className="mt-1.5 text-xs text-red-600">
                {falta}
              </p>
            )}

            <p className="mt-4 text-xs font-medium text-neutral-500">Destino</p>
            {/* Agrupados por a qué se publica: una tienda online y una red
                social no compiten entre sí, y en una lista plana había que leer
                las cuatro para encontrar la que se busca. */}
            <div data-tour="tienda-destino" className="mt-2 space-y-3">
              {canalesPorGrupo(CANALES.filter((c) => c.id !== 'mercadolibre' || ml.disponible)).map((g) => (
                <div key={g.id}>
                  <p className="mb-1.5 text-[11px] font-medium text-neutral-400">{g.titulo}</p>
                  {g.id === 'tienda' && ml.disponible && (
                    <p className="mb-1.5 text-[11px] text-neutral-400">
                      Tienda Nube y Mercado Libre se pueden elegir juntas: se publica en las dos.
                    </p>
                  )}
                  <div className="space-y-2">
                    {g.canales.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        disabled={!c.sePuedeArmar}
                        onClick={() => elegir(c.id)}
                        aria-pressed={elegidos.includes(c.id)}
                        className={`flex w-full items-center gap-3 rounded-xl border py-3 px-4 text-left transition-colors ${
                          !c.sePuedeArmar
                            ? 'cursor-not-allowed border-neutral-200 opacity-45'
                            : elegidos.includes(c.id)
                              ? 'border-brand-black bg-neutral-50'
                              : 'border-neutral-200 hover:bg-neutral-50'
                        }`}
                      >
                        <IconoDeCanal canal={c.id} />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-brand-black">
                            {c.nombre}
                          </span>
                          <span className="block text-xs text-neutral-500">{c.nota}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setAbierto(false)}
                className="flex-1 rounded-xl border border-neutral-200 py-2.5 px-5 text-sm text-brand-black"
              >
                Cancelar
              </button>
              {/* Sin nombre el botón se APRIETA y contesta, en vez de quedarse
                  apagado. Un botón deshabilitado no explica qué le falta: quien
                  no ve el campo vacío toca, no pasa nada, y no hay a dónde
                  mirar (ADR-2026-09-15-el-nombre-del-lanzamiento-se-pide-al-crear). */}
              <button
                type="button"
                onClick={() => void crear()}
                disabled={creando}
                className="flex-1 rounded-xl bg-brand-black py-2.5 px-5 text-sm font-medium text-white disabled:opacity-40"
              >
                {creando ? 'Creando…' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
